import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { Alert, AlertTitle, Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ChangeEvent, useRef, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

export default function UploadFileArea({
  setValue,
  label,
  name,
  acceptedFormats,
  acceptedFormatsLabel
}: {
  setValue: UseFormSetValue<any>
  name: string
  label: string
  acceptedFormats: string
  acceptedFormatsLabel: string
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [fileName, setFileName] = useState('')
  const [showError, setShowError] = useState(false)

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setShowError(false)
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > 3 * 1024 * 1024) {
      setFileName('')
      setShowError(true)
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      setValue(name, base64, { shouldDirty: true })
      setFileName(file.name)
    }
    reader.readAsDataURL(file)
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '.5rem',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1.5rem 1rem',
          width: '21.9375rem',
          height: '14rem',
          border: '1px solid #0000001F',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <UploadFileIcon color='info' />
        <Typography variant='subtitle1' color='info'>
          {label}
        </Typography>
        <Typography color='text.secondary' variant='caption'>
          {acceptedFormatsLabel}
        </Typography>
        <VisuallyHiddenInput
          type='file'
          onChange={handleFileChange}
          ref={inputRef}
          accept={acceptedFormats}
        />
      </Box>
      {fileName && (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '.625rem' }}>
          <CheckCircleIcon color='success' />
          <Typography>{fileName}</Typography>
        </Box>
      )}
      <Alert
        severity='error'
        sx={{ visibility: showError ? 'visible' : 'hidden' }}
      >
        <AlertTitle>{t('common.invalid_logo.title')}</AlertTitle>
        {t('common.invalid_logo.description')}
      </Alert>
    </Box>
  )
}
